// Left nav: all 41 pages by displayName, original pageOrder respected.
// Tooltip pages (small canvas + "Tooltip" in name) collapse into a "Tooltips"
// group at the bottom.

import { useMemo, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { usePagesIndex } from "../api/hooks";
import type { PageSpec } from "../types/pbi";
import { HSQ_PAGE_INDICES, TOOLTIP_ONLY_INDICES } from "../config/hsqPages";
import "./SideNav.css";

function isTooltip(p: PageSpec): boolean {
  const nameHasTooltip = /tooltip/i.test(p.displayName) || /tooltip/i.test(p.name);
  const small = p.width <= 900 && p.height <= 600;
  return nameHasTooltip && small;
}

export function SideNav() {
  const { data, isLoading, error } = usePagesIndex();
  const { folder: activeFolder } = useParams<{ folder?: string }>();
  const [tooltipsOpen, setTooltipsOpen] = useState(false);

  const { primary, tooltips } = useMemo(() => {
    const pages = data?.pages ?? [];
    // Respect original pageOrder via the spec's `index` (a.k.a. pageOrder).
    const sorted = [...pages].sort((a, b) => a.index - b.index);
    // P0: HSQ scoping — keep only pages in the HSQ allowlist, and additionally
    // suppress tooltip-only entries (those that exist only so tooltip-popover
    // fetches resolve). The legacy `isTooltip()` heuristic still backfills the
    // collapsed "Tooltips" group below, but with TOOLTIP_ONLY_INDICES the
    // filter is already authoritative.
    const inScope = sorted.filter((p) => HSQ_PAGE_INDICES.has(p.index));
    const visible = inScope.filter((p) => !TOOLTIP_ONLY_INDICES.has(p.index));
    return {
      primary: visible.filter((p) => !isTooltip(p)),
      tooltips: visible.filter(isTooltip),
    };
  }, [data]);

  // V1: single-page mode — when the HSQ allowlist resolves to a lone primary
  // entry (just "HSQ"), the side nav is visual clutter. Hide the entire aside
  // and let the PageView reclaim the freed-up canvas width. The check runs
  // BEFORE the loading/error early-returns so we don't briefly flash an empty
  // "Loading pages…" rail while the index fetch resolves.
  const isSinglePage = primary.length <= 1 && tooltips.length === 0;
  if (isSinglePage && !isLoading && !error) {
    return null;
  }

  if (isLoading) {
    return <aside className="sidenav"><div className="sidenav-status">Loading pages…</div></aside>;
  }
  if (error) {
    return <aside className="sidenav"><div className="sidenav-status sidenav-status-error">Failed to load pages</div></aside>;
  }

  return (
    <aside className="sidenav">
      <ul className="sidenav-list">
        {primary.map((p) => (
          <li key={p.folder}>
            <NavLink
              to={`/p/${encodeURIComponent(p.folder)}`}
              className={({ isActive }) =>
                isActive || p.folder === activeFolder
                  ? "sidenav-link is-active"
                  : "sidenav-link"
              }
              title={p.displayName}
            >
              <span className="sidenav-link-label">{p.displayName}</span>
            </NavLink>
          </li>
        ))}
      </ul>

      {tooltips.length > 0 && (
        <div className="sidenav-tooltips">
          <button
            type="button"
            className="sidenav-group-header"
            onClick={() => setTooltipsOpen((o) => !o)}
            aria-expanded={tooltipsOpen}
          >
            <span>{tooltipsOpen ? "▾" : "▸"}</span>
            <span>Tooltips ({tooltips.length})</span>
          </button>
          {tooltipsOpen && (
            <ul className="sidenav-list sidenav-list-sub">
              {tooltips.map((p) => (
                <li key={p.folder}>
                  <NavLink
                    to={`/p/${encodeURIComponent(p.folder)}`}
                    className={({ isActive }) =>
                      isActive ? "sidenav-link is-active" : "sidenav-link"
                    }
                  >
                    <span className="sidenav-link-label">{p.displayName}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </aside>
  );
}
